import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import PremiumGate from '../../components/PremiumGate';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LoadingState from '../../components/ui/LoadingState';
import ScriptTypeSelector from './components/ScriptTypeSelector';
import SituationInputForm from './components/SituationInputForm';
import GeneratedScript from './components/GeneratedScript';
import ScriptLibrary from './components/ScriptLibrary';
import TemplateLibraryModal from './components/TemplateLibraryModal';
import { scriptService } from '../../services/scriptService';
import { useAuth } from '../../contexts/AuthContext';

const ScriptLab = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [activeView, setActiveView] = useState('create');
  const [selectedType, setSelectedType] = useState('email');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedScript, setGeneratedScript] = useState(null);
  const [cachedScriptId, setCachedScriptId] = useState(null); // NEW: Track cache ID for rating
  const [formData, setFormData] = useState({
    prospectName: '',
    stage: '',
    background: '',
    interactions: '',
    outcome: '',
    painPoints: ''
  });

  // Load saved scripts from Supabase
  const [savedScripts, setSavedScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);

  useEffect(() => {
    loadSavedScripts();
  }, []);

  const loadSavedScripts = async () => {
    try {
      setLoading(true);
      const scripts = await scriptService?.getAll();
      setSavedScripts(scripts);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading scripts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateScript = async () => {
    if (!formData?.prospectName || !formData?.stage || !formData?.outcome) {
      setError('⚠️ Please fill in all required fields: Prospect Name, Sales Stage, and Desired Outcome');
      return;
    }

    setIsGenerating(true);
    setError('');
    setCachedScriptId(null); // Reset cache ID

    try {
      // Database-first generation with AI fallback
      const script = await scriptService?.generateScript({
        scriptType: selectedType,
        industry: profile?.industry || 'General',
        prospectName: formData?.prospectName,
        stage: formData?.stage,
        background: formData?.background,
        interactions: formData?.interactions,
        outcome: formData?.outcome,
        painPoints: formData?.painPoints
      });

      setGeneratedScript(script);

      // If AI-generated (not from template), cache it for rating
      if (!script?.fromTemplate) {
        const scenarioText = `${formData?.stage} - ${formData?.outcome}`;
        const cachedScript = await scriptService?.cacheAIScript({
          scriptType: selectedType,
          industry: profile?.industry || 'General',
          scenarioText,
          generatedScript: script?.content,
          toneNotes: script?.toneNotes
        });

        if (cachedScript?.id) {
          setCachedScriptId(cachedScript?.id);
          console.log('✅ Script cached with ID:', cachedScript?.id);
        }
      }
      
      // Show indicator if template was used
      if (script?.fromTemplate) {
        console.log('✅ Used database template for instant generation');
      }
    } catch (err) {
      // Display the actual error message from the service
      const errorMessage = err?.message || 'Failed to generate script. Please try again.';
      setError(errorMessage);
      console.error('Error generating script:', err);
      
      // Scroll to error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRateScript = async (rating) => {
    if (!cachedScriptId) {
      alert('Cannot rate this script (not AI-generated or missing ID)');
      return;
    }

    try {
      const result = await scriptService?.rateScript(cachedScriptId, rating);
      
      if (result?.promoted) {
        alert('🎉 Great! This script has been promoted to the Script Library and will be used for future generations!');
      } else if (rating === 5) {
        alert('✅ Thank you for the positive feedback!');
      } else {
        alert('📝 Thank you for the feedback. We will keep improving!');
      }

      // Clear cached script ID after rating
      setCachedScriptId(null);
    } catch (err) {
      setError(err?.message || 'Failed to rate script');
      console.error('Error rating script:', err);
    }
  };

  const handleSaveScript = async () => {
    if (!generatedScript) return;

    try {
      const title = `${formData?.stage?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')} - ${formData?.prospectName}`;
      
      const saved = await scriptService?.saveScript({
        scriptType: generatedScript?.scriptType,
        title,
        industry: generatedScript?.industry,
        situation: generatedScript?.situation,
        subject: generatedScript?.subject,
        content: generatedScript?.content,
        toneNotes: generatedScript?.toneNotes
      });

      setSavedScripts([saved, ...savedScripts]);
      alert('Script saved to your playbook!');
    } catch (err) {
      setError(err?.message);
      console.error('Error saving script:', err);
    }
  };

  const handleEditScript = () => {
    alert('Edit functionality would open a modal with editable script content');
  };

  const handleRegenerateScript = () => {
    setGeneratedScript(null);
    handleGenerateScript();
  };

  const handleViewScript = (script) => {
    setGeneratedScript({
      scriptType: script?.scriptType,
      industry: script?.industry,
      subject: script?.subject,
      content: script?.content,
      toneNotes: script?.toneNotes,
      situation: script?.situation
    });
    setActiveView('create');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteScript = async (scriptId) => {
    if (window.confirm('Are you sure you want to delete this script?')) {
      try {
        await scriptService?.delete(scriptId);
        setSavedScripts(savedScripts?.filter(s => s?.id !== scriptId));
      } catch (err) {
        setError(err?.message);
        console.error('Error deleting script:', err);
      }
    }
  };

  const handleResetForm = () => {
    setFormData({
      prospectName: '',
      stage: '',
      background: '',
      interactions: '',
      outcome: '',
      painPoints: ''
    });
    setGeneratedScript(null);
    setCachedScriptId(null); // Reset cache ID
    setSelectedType('email');
  };

  const handleUseTemplate = (template) => {
    // Pre-fill form with template placeholders as hints
    setFormData({
      prospectName: '{prospect_name}',
      stage: template?.goal?.replace(/_/g, '-'),
      background: 'Enter prospect background here',
      interactions: 'Describe previous interactions',
      outcome: 'What outcome are you looking for?',
      painPoints: 'Key pain points or concerns'
    });

    // Set the template text as a starting point for AI generation
    setGeneratedScript({
      scriptType: template?.scriptType,
      industry: template?.industry,
      subject: template?.title,
      content: template?.templateText,
      toneNotes: template?.coachingNotes,
      situation: `Template: ${template?.goal}`
    });

    setShowTemplateLibrary(false);
    setActiveView('create');
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar userStreak={7} />
      <main className="main-content max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Icon name="FileText" size={24} color="#FFFFFF" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Script Lab</h1>
              <p className="text-sm text-muted-foreground">Generate personalized sales scripts powered by Lumo AI</p>
            </div>
          </div>
        </div>

        <PremiumGate feature="scriptLab" requiredPlan="Pro">
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-error mb-1">Error Generating Script</h3>
                  <p className="text-sm text-error/90 whitespace-pre-wrap">{error}</p>
                {error?.includes('API key') && (
                  <div className="mt-3 p-3 bg-background/50 rounded border border-error/20">
                    <p className="text-xs text-muted-foreground mb-2">
                      <strong>How to fix:</strong>
                    </p>
                    <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                      <li>Add <code className="px-1 py-0.5 bg-muted rounded text-xs">VITE_AIMLAPI_KEY=your-key-here</code> to your .env file</li>
                      <li>Get your API key from <a href="https://aimlapi.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">aimlapi.com</a></li>
                      <li>Restart your development server after adding the key</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveView('create')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeView === 'create' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="Plus" size={16} />
              <span>Create Script</span>
            </div>
          </button>
          <button
            onClick={() => setActiveView('library')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeView === 'library' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="BookOpen" size={16} />
              <span>My Playbook ({savedScripts?.length})</span>
            </div>
          </button>
        </div>

        {activeView === 'create' ? (
          <div className="space-y-6">
            {!generatedScript ? (
              <>
                <div className="bg-card rounded-xl border border-border p-6 space-y-6">
                  <div className="flex items-start gap-3 p-4 bg-accent/10 rounded-lg border border-accent/20">
                    <Icon name="Lightbulb" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">Smart Script Generation</h3>
                      <p className="text-xs text-muted-foreground">
                        We'll check our template library first for instant results. If no match, Lumo AI will create a custom script tailored to your needs.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-accent/10 rounded-lg border border-accent/20">
                    <Icon name="BookOpen" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-foreground mb-1">Try a Proven Template</h3>
                      <p className="text-xs text-muted-foreground mb-3">
                        Browse our library of high-converting script templates for {selectedType} messages
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Sparkles"
                        iconPosition="left"
                        onClick={() => setShowTemplateLibrary(true)}
                      >
                        Browse Templates
                      </Button>
                    </div>
                  </div>

                  {profile?.industry && (
                    <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <Icon name="Briefcase" size={18} color="var(--color-primary)" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">Your Industry</p>
                        <p className="text-sm font-medium text-foreground capitalize">{profile?.industry}</p>
                      </div>
                      <button
                        onClick={() => navigate('/app/settings')}
                        className="text-xs text-primary hover:text-primary/80 font-medium"
                      >
                        Change
                      </button>
                    </div>
                  )}

                  <ScriptTypeSelector selectedType={selectedType} onTypeChange={setSelectedType} />
                  
                  <SituationInputForm formData={formData} onChange={setFormData} />

                  <div className="flex gap-3 pt-4 border-t border-border">
                    <Button
                      variant="default"
                      iconName="Sparkles"
                      iconPosition="left"
                      onClick={handleGenerateScript}
                      loading={isGenerating}
                      disabled={isGenerating}
                    >
                      {isGenerating ? 'Generating Script...' : 'Generate with Lumo'}
                    </Button>
                    <Button variant="outline" iconName="RotateCcw" onClick={handleResetForm}>
                      Reset Form
                    </Button>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border border-primary/20 p-6">
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
                    Quick Tips for Better Scripts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Target" size={16} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">Be Specific</h4>
                        <p className="text-xs text-muted-foreground">Include detailed prospect background and pain points for more personalized scripts</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="MessageSquare" size={16} color="var(--color-accent)" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">Match the Medium</h4>
                        <p className="text-xs text-muted-foreground">SMS for quick follow-ups, email for detailed proposals, calls for discovery</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Users" size={16} color="var(--color-success)" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">Know Your Audience</h4>
                        <p className="text-xs text-muted-foreground">Different industries and sales stages require different approaches</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Clock" size={16} color="var(--color-warning)" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">Timing Matters</h4>
                        <p className="text-xs text-muted-foreground">Reference previous interactions and propose specific next steps with timeframes</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <GeneratedScript
                script={generatedScript}
                onSave={handleSaveScript}
                onEdit={handleEditScript}
                onRegenerate={handleRegenerateScript}
                onRate={handleRateScript}
                canRate={!!cachedScriptId}
              />
            )}
          </div>
        ) : loading ? (
          <LoadingState message="Loading your playbook..." />
        ) : (
          <ScriptLibrary
            scripts={savedScripts}
            onViewScript={handleViewScript}
            onEditScript={handleEditScript}
            onDeleteScript={handleDeleteScript}
          />
        )}
        
        <TemplateLibraryModal
          isOpen={showTemplateLibrary}
          onClose={() => setShowTemplateLibrary(false)}
          onUseTemplate={handleUseTemplate}
          selectedType={selectedType}
        />
        </PremiumGate>
      </main></div>
  );
};

export default ScriptLab;